import {
  Assessment,
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  GetAssessmentsQueryFilters,
  GetAssessmentsQuerySort,
  RepetitionType,
  SORT_DIRECTION,
  useGetAssessmentsQuery,
  useGetGroupsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { AssessmentStatuses } from '@schooly/constants';
import { addDays, format } from 'date-fns';
import { useEffect, useMemo } from 'react';

import useSchoolYears from '../../../../hooks/useSchoolYears';
import { newDateTimezoneOffset } from '@schooly/utils/date';

import { buildStaffGroupsQueryParams } from '../utils';

export const ASSESSMENTS_WIDGET_TYPES = ['awaiting-completion', 'upcoming'] as const;
export type AssessmentsWidgetType = (typeof ASSESSMENTS_WIDGET_TYPES)[number];

export const ASSESSMENTS_WIDGET_PAGE_SIZE = 5;

export const useAssessmentsWidgetQuery = (type: AssessmentsWidgetType) => {
  const { schoolId = '', relationId = '' } = useAuth();
  const { defaultValidity } = useSchoolYears();

  // Same `useGetGroupsQuery` cache as `useGroupsWidgetData` (refetch there: `'always'`; here `false`).
  // Change params/options in both or share one hook.
  const { data: staffGroupsData, isLoading: isStaffGroupsLoading } = useGetGroupsQuery(
    buildStaffGroupsQueryParams({ schoolId, relationId }),
    { enabled: !!relationId, refetchOnMount: false },
  );

  const staffGroupIds = useMemo(
    () => (staffGroupsData?.pages ?? []).flatMap((page) => page.results.map((g) => g.id)),
    [staffGroupsData?.pages],
  );

  const { filters, sort } = useMemo(() => {
    const today = format(newDateTimezoneOffset(), DEFAULT_DATE_FORMAT_FNS);
    const tomorrow = format(addDays(newDateTimezoneOffset(), 1), DEFAULT_DATE_FORMAT_FNS);
    const yearStart = defaultValidity?.start ?? '';
    const yearEnd = defaultValidity?.end ?? '';

    const dateRange = type === 'awaiting-completion' ? [yearStart, today] : [tomorrow, yearEnd];

    const filters: GetAssessmentsQueryFilters = {
      [FilterKeys.Staff]: [relationId],
      [FilterKeys.AssessmentStatus]: [AssessmentStatuses['Not published']],
      ...(type === 'upcoming' && {
        [FilterKeys.RepetitionType]: [RepetitionType.NextFromDatePerRecurrence],
      }),
      [FilterKeys.Date]: dateRange,
      [FilterKeys.Group]: staffGroupIds,
    };

    const sort: GetAssessmentsQuerySort[] = [
      {
        columnTextId: 'assessment_date',
        direction: SORT_DIRECTION.ASC,
      },
      { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
    ];

    return { filters, sort };
  }, [defaultValidity?.end, defaultValidity?.start, relationId, staffGroupIds, type]);

  const hasStaffGroups = staffGroupIds.length > 0;
  const enabled = hasStaffGroups && !!defaultValidity;

  const {
    data,
    isFetched: isAssessmentsFetched,
    setParams,
  } = useGetAssessmentsQuery(
    {
      school_id: schoolId,
      filters,
      query: '',
      sort,
      page_size: ASSESSMENTS_WIDGET_PAGE_SIZE,
    },
    { refetchOnMount: 'always', enabled },
  );

  useEffect(() => {
    setParams((prev) => ({ ...prev, filters, sort, query: '' }));
  }, [filters, setParams, sort]);

  const isLoading = isStaffGroupsLoading || (enabled && !isAssessmentsFetched);
  const isFetched = !isLoading;

  const assessments: Assessment[] = useMemo(
    () =>
      data?.pages?.flatMap((page) =>
        page.results.map((a) => ({
          ...a,
          groups: a.groups.filter((g) => staffGroupIds.includes(g.id)),
        })),
      ) ?? [],
    [data?.pages, staffGroupIds],
  );

  const total = data?.pages?.[0]?.count ?? 0;

  return { assessments, total, isLoading, isFetched, filters };
};
